/**
 * 진행(영구 강화) 커맨드 (일감 S2b) — 스탯 강화·환생·유물 획득 (Phaser-free).
 * GameScene upgradeStat/performRebirth/upgradeRebirthStat/acquireRelic 추출.
 * 컨트롤러는 ctx getter 경유, 셸 부수효과는 ShellPorts.
 */
import { BigNumber } from "@yjsgames/bignumber";
import { resolvePurchase, type Multiplier } from "../../systems/multiBuy.js";
import { RELIC_DEFS, MODULE_BRICK_BALANCE } from "../../data/balance.js";
import type { ModuleMasteryBranch } from "../../data/moduleMasteryDefinitions.js";
import type { GameContext } from "../GameContext.js";
import type { ShellPorts } from "./ports.js";
import { chapterForWorld, resolveWorldForChapter, WORLD_CHAPTER_SPAN, WORLD_DEFS } from "../../data/worldData.js";

const VERBOSE_RELIC = false;

export interface ProgressionCommandDeps {
  ctx: GameContext;
  ports: ShellPorts;
  /** 유물 효과의 전투 반영값(baseDamage·fireMs) 문자열 — Verbose 진단 전용 (셸 제공). */
  combatProbe?: () => string;
  /** 성장 구매 성공 알림 — 가이드퀘스트 목표 진행. */
  onUpgradeBought?: (target: string) => void;
  /** 기능 수행 성공 알림 — 가이드퀘스트 actionCompleted 목표 진행. */
  onActionCompleted?: (target: string) => void;
}

export class ProgressionCommands {
  constructor(private readonly deps: ProgressionCommandDeps) {}

  /**
   * 스탯 업그레이드 — 선택 배수(x1/x10/x100/xMax)만큼 일괄 강화 (스탯 탭).
   * multiBuy.resolvePurchase로 구매 수량/총비용을 계산 → 총비용 차감 → 일괄 레벨 증가.
   * multiplier는 뷰 상태(MultiplierSelector)라 호출자(설치자)가 전달한다.
   */
  upgradeStat(statId: string, multiplier: Multiplier): void {
    const { ctx, ports } = this.deps;
    const statUpgrade = ctx.statUpgrade();
    if (statUpgrade.isMaxed(statId)) return;
    const { count, totalCost } = resolvePurchase(
      (lv) => statUpgrade.unitCostAt(statId, lv),
      statUpgrade.level(statId),
      statUpgrade.maxLevelOf(statId),
      ctx.economy().gold,
      multiplier,
    );
    if (count <= 0) return; // 보유 골드로 1레벨도 못 삼 (버튼은 비활성 상태)
    if (!ctx.economy().trySpend(totalCost)) return;
    statUpgrade.upgradeMany(statId, count);
    // fireRate 강화는 발사 루프 간격을 즉시 재설정 (다음 발사부터 빨라짐).
    if (statId === "fireRate") ports.resetFireTimer();
    ctx.soundController().play("levelup");
    this.deps.onUpgradeBought?.(statId);
    this.deps.onActionCompleted?.("statUpgrade.buy");
    ports.invalidateUi();
    ports.persist();
  }

  /** 환생 — 포인트 적립(챕터 비례+포인트보너스 가산) 후 골드/챕터/전장 리셋. */
  performRebirth(): void {
    const { ctx, ports } = this.deps;
    if (!ctx.rebirth().canRebirth(ctx.chapter().currentChapter)) return;
    ctx.rebirth().performRebirth(
      ctx.chapter().bestChapter,
      ctx.rebirthUpgrade().effectOf("pointBonus"),
    );
    ctx.analytics().log("rebirth", { best_chapter: ctx.chapter().bestChapter });
    ctx.soundController().play("rebirth");
    ctx.economy().setGold(BigNumber.ZERO);
    ctx.goldBox().resetPriceProgress(); // 골드 상자 가격 누진은 회차별 성장으로 취급해 환생 때 초기화.
    ctx.statUpgrade().resetAll(); // 골드 스탯 강화 초기화 (환생 강화 rebirthUpgrade는 유지)
    ctx.moduleMastery().clear(); // 회차 숙련도 XP/분기 선택 초기화
    ctx.inventory().clear(); // 보유 모듈 초기화
    ctx.grid().clearPlacedModules(); // 그리드 배치 모듈 초기화 (해금 상태는 유지)
    ctx.spaceship().setModuleUnlockedCells(
      ctx.spaceship().selectedId,
      ctx.grid().toState().unlockedCells,
    ); // 우주선별 슬롯 해금 소유는 환생 후에도 유지
    ctx.chapter().resetToChapterOne();
    // ⚠ playerModuleLevels(모듈 레벨)·upgradeInv(강화석)은 의도적으로 리셋하지 않음 — 환생 유지 영구 자산.
    //    등급(인스턴스)은 inventory.clear로 소멸하나 레벨/강화석은 환생 너머로 보존(2축 분리, 명세 P0).
    ports.resetBattlefield();
    this.deps.onActionCompleted?.("rebirth.perform");
    ports.invalidateUi();
    ports.persistNow(); // 환생(비가역 진행 리셋) — 즉시 저장(유실 시 진행 손실)
  }

  /**
   * 환생 포인트 → 강화석 교환 (강화석 수급 P1). 포인트 1 충분 시 차감 + shop.tsv 비율만큼 강화석 지급.
   * 강화석은 환생 유지 자산이므로 환생으로 쌓은 포인트를 영구 모듈 레벨 성장으로 전환하는 경로.
   */
  exchangeRebirthPointForStone(): void {
    const { ctx, ports } = this.deps;
    if (!ctx.rebirth().spendPoint(1)) return; // 환생 포인트 부족
    ctx.upgradeInv().addStone(MODULE_BRICK_BALANCE.stonePerRebirthPoint);
    ctx.soundController().play("box");
    this.deps.onUpgradeBought?.("rebirthPointStone");
    ports.invalidateUi();
    ports.persist();
  }

  /**
   * 환생 스탯 강화 — 선택 배수(x1/x10/x100/xMax)만큼 환생 포인트를 소모해 일괄 강화.
   * fireRate가 1레벨 이상 오르면 발사 타이머를 즉시 재설정한다.
   */
  upgradeRebirthStat(statId: string, multiplier: Multiplier = 1): void {
    const { ctx, ports } = this.deps;
    const rebirthUpgrade = ctx.rebirthUpgrade();
    if (rebirthUpgrade.isMaxed(statId)) return;
    const { count, totalCost } = resolvePurchase(
      (lv) => rebirthUpgrade.unitCostAt(statId, lv),
      rebirthUpgrade.level(statId),
      rebirthUpgrade.maxLevelOf(statId),
      BigNumber.fromNumber(ctx.rebirth().rebirthPoint),
      multiplier,
    );
    const cost = totalCost.toNumber();
    if (count <= 0) return;
    if (!ctx.rebirth().spendPoint(cost)) return; // 환생 포인트 부족
    const applied = rebirthUpgrade.upgradeMany(statId, count);
    if (applied <= 0) return;
    if (statId === "fireRate") ports.resetFireTimer();
    ctx.soundController().play("levelup");
    this.deps.onUpgradeBought?.(statId);
    this.deps.onActionCompleted?.("rebirth.upgrade");
    ports.invalidateUi();
    ports.persist();
  }

  /**
   * 모듈 숙련도 분기 선택 (숙련도 탭 A/B 버튼) — 레벨 3 분기 노드 중 적용할 한쪽 지정.
   * 비용 없음(획득 분기 선택일 뿐). 미해금 분기도 선택 가능하며, 카운터 임계 도달 시 효과가 발동된다.
   */
  selectMasteryBranch(moduleId: string, branch: ModuleMasteryBranch): void {
    const { ctx, ports } = this.deps;
    ctx.moduleMastery().selectBranch(moduleId, branch);
    ctx.soundController().play("levelup");
    this.deps.onUpgradeBought?.("moduleMastery");
    this.deps.onActionCompleted?.("mastery.branchSelect");
    ports.invalidateUi();
    ports.persist();
  }

  /**
   * 월드 이동 — 클리어된 다른 월드로 이동(환생과 동일 범위 리셋, 환생 포인트 미지급).
   * bestChapter는 유지 → 이미 클리어한 월드는 여전히 해금 상태.
   * 가드: 현재 월드면 no-op, 미클리어/미래 월드면 no-op.
   */
  moveToWorld(worldNo: number): void {
    const { ctx, ports } = this.deps;
    const currentWorld = resolveWorldForChapter(
      ctx.chapter().currentChapter,
      WORLD_CHAPTER_SPAN,
      WORLD_DEFS,
    );
    // 현재 월드면 no-op
    if (worldNo === currentWorld.worldNo) return;
    const targetStartChapter = chapterForWorld(worldNo);
    const targetClearThreshold = targetStartChapter + WORLD_CHAPTER_SPAN;
    // 해당 월드의 마지막 챕터를 클리어하고 다음 월드에 진입한 적이 없으면 no-op.
    if (ctx.chapter().bestChapter < targetClearThreshold) return;
    // 환생과 동일 범위 리셋 (환생 포인트/카운트 미지급)
    ctx.economy().setGold(BigNumber.ZERO);
    ctx.goldBox().resetPriceProgress();
    ctx.statUpgrade().resetAll();
    ctx.moduleMastery().clear();
    ctx.inventory().clear();
    ctx.grid().clearPlacedModules();
    ctx.spaceship().setModuleUnlockedCells(
      ctx.spaceship().selectedId,
      ctx.grid().toState().unlockedCells,
    );
    // 목표 월드의 챕터 1로 리셋
    ctx.chapter().resetToChapter(targetStartChapter);
    ctx.analytics().log("world_move", { from: currentWorld.worldNo, to: worldNo });
    ctx.soundController().play("rebirth");
    ports.resetBattlefield();
    this.deps.onActionCompleted?.("world.move");
    ports.invalidateUi();
    ports.persistNow();
  }

  /** 유물 획득 — 골드 충족 시 골드 차감 → 1개 획득(누적·레벨 +1, 무제한) (유물 탭). */
  acquireRelic(relicId: string): void {
    const { ctx, ports } = this.deps;
    const def = RELIC_DEFS[relicId];
    if (!def) return;
    const relic = ctx.relic();
    if (VERBOSE_RELIC)
      console.log(
        `[ProgressionCommands][V][acquireRelic] try ${relicId} cost=${def.cost} gold=${ctx.economy().gold.toString()}`,
      );
    if (!ctx.economy().trySpend(BigNumber.fromNumber(def.cost))) {
      if (VERBOSE_RELIC)
        console.log(`[ProgressionCommands][V][acquireRelic] 골드 부족 — 차감 실패 ${relicId}`);
      return;
    }
    relic.acquire(relicId);
    // 쿨타임 유물은 발사 루프 간격을 즉시 재설정 (fireIntervalMs가 곱하므로 reset 없이는 미반영).
    if (def.effectType === "fireRatePercent") ports.resetFireTimer();
    if (VERBOSE_RELIC)
      console.log(
        `[ProgressionCommands][V][acquireRelic] ${relicId} Lv${relic.level(relicId)}(x${relic.count(relicId)}) kinds=${relic.ownedKindCount} ` +
          `atk×${relic.attackMultiplier().toFixed(3)} gold×${relic.goldMultiplier().toFixed(3)} ` +
          `cd×${relic.fireRateMultiplier().toFixed(3)} spd×${relic.speedMultiplier().toFixed(3)}` +
          (this.deps.combatProbe ? ` | ${this.deps.combatProbe()}` : ""),
      );
    ctx.soundController().play("levelup");
    this.deps.onUpgradeBought?.(relicId);
    this.deps.onActionCompleted?.("relic.acquire");
    ports.invalidateUi();
    ports.persist();
  }
}
